import { registerSessionReset } from 'stores/sessionResetRegistry';
import { storage, StorageKey } from 'utils/typedStorage';

interface TeamsSsoSession {
  identity: string;
  acquire: () => Promise<string>;
  refreshAfter: number;
  pending: Promise<void> | null;
}

let session: TeamsSsoSession | null = null;
let teamsToken: string | null = null;
let activeSessionIdentity: string | null = null;
let sessionSequence = 0;

const nextSessionIdentity = (): string => `teams-session:${++sessionSequence}`;

/** Expiry is a scheduling hint only; the backend still verifies every credential. */
function refreshAfter(token: string): number {
  const now = Date.now();
  const payload = token.split('.')[1];
  if (payload) {
    try {
      const decoded: unknown = JSON.parse(
        atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
      );
      if (
        typeof decoded === 'object' &&
        decoded !== null &&
        'exp' in decoded &&
        typeof decoded.exp === 'number' &&
        Number.isFinite(decoded.exp)
      ) {
        return Math.min(decoded.exp * 1000 - 60_000, now + 300_000);
      }
    } catch {
      // swallow-ok: A missing expiry only disables long caching; token authenticity is checked by the backend. Do not log parsing errors, which can include credential fragments.
    }
  }
  return now + 30_000;
}

function acquireToken(current: TeamsSsoSession): Promise<void> {
  if (current.pending) return current.pending;
  current.pending = Promise.resolve()
    .then(current.acquire)
    .then((token) => {
      if (session !== current)
        throw new Error('Teams session changed during authentication');
      if (!token) throw new Error('Teams did not return an access token');
      teamsToken = token;
      activeSessionIdentity = current.identity;
      current.refreshAfter = refreshAfter(token);
    })
    .catch((error: unknown) => {
      if (session === current) {
        // Retain the provider for retry, but never send the expired credential.
        teamsToken = null;
        current.refreshAfter = 0;
      }
      throw error;
    })
    .finally(() => {
      current.pending = null;
    });
  return current.pending;
}

/** Keep acquisition available after the launcher navigates to the live note. */
export function startTeamsSsoSession(
  acquire: () => Promise<string>
): Promise<void> {
  const current: TeamsSsoSession = {
    identity: nextSessionIdentity(),
    acquire,
    refreshAfter: 0,
    pending: null,
  };
  session = current;
  teamsToken = null;
  activeSessionIdentity = null;
  return acquireToken(current);
}

/** No timer: refresh immediately before transport needs a Teams credential. */
export function refreshTeamsSsoToken(): Promise<void> | undefined {
  if (!session || storage.get(StorageKey.AuthToken)) return undefined;
  if (getTeamsToken() && Date.now() < session.refreshAfter) return undefined;
  return acquireToken(session);
}

export function clearTeamsSsoSession(): void {
  session = null;
  teamsToken = null;
  activeSessionIdentity = null;
}

/** UI/cache identity survives credential renewal and transient acquisition failures. */
export function getTeamsSessionId(): string | null {
  return activeSessionIdentity;
}

/** Preserve the synchronous credential-store API used by all transports. */
export function getTeamsToken(): string | null {
  return teamsToken;
}

/** An explicit reset also prevents an in-flight renewal from restoring the session. */
export function setTeamsToken(token: string | null): void {
  if (token === null) clearTeamsSsoSession();
  else {
    teamsToken = token;
    activeSessionIdentity ??= session?.identity ?? nextSessionIdentity();
  }
}

registerSessionReset(clearTeamsSsoSession);
